import Vue from 'vue'
import VueRouter from 'vue-router'
import config from '@config'
import { encodeGetParams } from '@ota-frontend/core/helpers/url'
import {
  createSearchConditionFromQuery,
  buildQueryFromSearchCondition,
  makeDetailPageUrl,
  makeSearchAgainUrl,
  validateAndUpdateParameters
} from './query'
import TourListPage from '@/pages/TourListPage.vue'
import TourLp from '@/pages/TourLp.vue'
import PageNotFound from '@/pages/PageNotFound.vue'

Vue.use(VueRouter)

const passiveParameters = ['research']

const routes = [
  // LP
  {
    path: '/japan/tour/lp/:name',
    name: 'lp',
    component: TourLp,
    props: (route) =>
      ((condition) => ({
        name: route.params.name,

        mobile: seemsToBeMobileDevice(),

        searchCondition: condition,

        updateSearchCondition: updateSearchCondition(route.query),

        makeDetailPageUrl: makeDetailPageUrl(
          route.query, // ota-frontend で詳細画面を実装するまでは念のため空文字等も引き継いでおく
          config.pageUrl.onlineTourDetail,
          config.pageUrl.offlineTourDetail,
          route.path
        ),

        makeSearchAgainUrl,

        onMissingDefinition: () =>
          router.push({ name: 'page-not-found' }).catch(() => {}),
      }))(createSearchConditionFromQuery(route.query)),
  },

  // ツアー一覧
  {
    path: '/tour/tourresult',
    alias: '/japan/tour/*', // seo 用 URL /japan/tour/hokkaido/sapporo/ 等
    name: 'tour-list',
    component: TourListPage,
    // trigger function to validate query params before the route is entered
    // overwrite the path and query parameters
    async beforeEnter(to, from, next) {
      if (to.meta.validated) {
        next();
        return;
      }
      const validatedQuery = await validateAndUpdateParameters(to.query);
      if (validatedQuery) {
        to.meta.validated = true;
        next({ path: to.path, query: validatedQuery });
      }
    },
    props: (route) =>
      ((condition) => ({
        mobile: seemsToBeMobileDevice(),

        searchCondition: condition,

        updateSearchCondition: updateSearchCondition(route.query),

        makeDetailPageUrl: makeDetailPageUrl(
          route.query, // ota-frontend で詳細画面を実装するまでは念のため空文字等も引き継いでおく
          config.pageUrl.onlineTourDetail,
          config.pageUrl.offlineTourDetail,
          route.path
        ),

        makeSearchAgainUrl,

        // seo URL の場合はこれを BFF に渡すことで都市コード等を解決してもらう
        // ページコンポーネント的には内容に依存した処理は一切しない想定
        seoUrlPath: route.path.startsWith('/japan/tour/') ? route.path : null,

        defaultTab:
          route.query.default_tab ||
          (condition.isDomestic ? 'online' : 'offline'),

        showResearch: route.query.show_research === '1',
      }))(createSearchConditionFromQuery(route.query)),
  },

  // 404
  {
    path: '*',
    name: 'page-not-found',
    component: PageNotFound,
  },
]

const seemsToBeMobileDevice = () => window.innerWidth <= 657

const pick = (keys) => (d) =>
  Object.fromEntries(Object.entries(d).filter(([k, _]) => keys.includes(k)))

const pickUpPassiveParameters = pick(passiveParameters)

const updateSearchCondition = (query) => (
  newCondition,
  options = {
    normalizeLocation: false, // trueの場合pathを正規化
    replace: false,
    passiveParameters: {},
  }
) => {
  const location = {
    ...(options.normalizeLocation ? { path: '/tour/tourresult' } : {}),
    query: buildQueryFromSearchCondition(newCondition, {
      ...pickUpPassiveParameters(query),
      ...options.passiveParameters,
    }),
  }

  // .catch(() => {}) について
  // クエリパラメータが変わっていない場合は NavigationDuplicated エラーになるため、簡易な方法で回避しておく
  // vue-router はURLに変化がない場合は NavigationDuplicated を throw して履歴追加はしない模様
  // クライアントエラーを収集する仕組みを導入したら NavigationDuplicated 以外は収集できるようにしておいた方が良い
  if (options.replace) {
    router.replace(location).catch(() => {})
  } else {
    router.push(location).catch(() => {})
  }
}

const router = new VueRouter({
  mode: 'history',
  base: config.baseRouterUrl || process.env.BASE_URL,
  stringifyQuery: function (query) {
    const queryString = encodeGetParams(query)
    return (queryString && `?${queryString}`) || ''
  },
  routes,
})

export default router
